/**
 * CORE — deterministic procedural course generation.
 *
 * Turns a 32-bit numeric seed into ONE explicit `CourseSpec`. This runs exactly
 * once per day; everyone else consumes the resulting element list as data, so
 * Core (physics) and Render (meshes) can never disagree about geometry.
 *
 * Determinism rules honored here:
 *   - The ONLY source of randomness is `makeRng(seed)` from `@/types`. We never
 *     touch Math.random, Date, or any platform global.
 *   - Draw order is fixed and total: the same seed always pulls the same numbers
 *     in the same order, so the same course falls out on client and Worker.
 *   - Output is pure data (plain tuples), fully JSON-serializable.
 *
 * Course model: a WINDING DRIVEABLE TRACK the player actively STEERS through. The
 * track is a sequence of SEGMENTS laid head-to-tail along a cursor whose HEADING
 * (yaw) changes again — gentle curves and sharper turns that escalate with
 * distance — so the player drives a winding course rather than a straight chute.
 * Turns are BANKED (the track surface rolls into the turn) so the carving line
 * holds. Sustained PITCH sections give real DOWNHILLS (gravity adds speed) and
 * uphills. Sealed, continuous side walls hug BOTH edges through every turn, so a
 * mis-steer bounces off a wall rather than instantly falling off (forgiving). The
 * track is WIDE so the turns are carveable at speed. DELIBERATE launch ramps — a
 * short up-wedge before a gap with a flat landing run — remain as an optional jump.
 * Difficulty escalates with distance: turns sharpen, hills steepen, the lane
 * narrows, and hazards / gaps / movers get denser while boosters reward a clean
 * line. Some segments spawn a parallel SHORTCUT: a narrow, hazard-guarded alternate
 * lane that rejoins downstream — classic risk/reward. A guaranteed LETHAL barrier
 * sits in the reachable opening and a safe walled start apron + booster launches
 * the run.
 *
 * Owned by: Core. Produces: CourseSpec (Contract #1).
 */

import { SCHEMA_VERSION, SIM_VERSION, makeRng } from '@/types';
import type {
  CourseSpec,
  CourseElement,
  CourseCheckpoint,
  TrackElement,
  RampElement,
  SurfaceTuning,
  PhysicsTuning,
  HazardEffect,
  Vec3,
  Quat,
  Bounds,
  Rng,
} from '@/types';
import { quatFromAxisAngle, quatRotate, v3 } from './vecmath';

/** Tunables for the generator's feel. Constants, not contract — but fixed so
 *  the same seed yields the same course. Changing any of these is a sim change
 *  and would require bumping SIM_VERSION. */
const GEN = {
  /** MUCH longer course (~5× the old 14–20): a multi-minute survival run at
   *  escalating speed. Element count stays bounded (a few thousand max) because
   *  each segment emits only a handful of slabs + walls. */
  segmentCount: { min: 80, max: 100 },
  /** Half-width of the main lane (X) at the START. WIDE so the player can carve the
   *  winding turns at speed (not an impossible thread). Narrows with progress (see
   *  `laneHalfWidthMin`) so later sections are tighter. */
  laneHalfWidth: 5.0,
  /** Narrowest the main lane gets at the very end of a long run. Kept generous (a
   *  driving line, not a tightrope) so late turns stay carveable. */
  laneHalfWidthMin: 3.2,
  /** Track slab half-thickness (Y). */
  slabHalfThickness: 0.5,
  /** Wall slab half-thickness (X, local). Thickened 0.3 -> 0.6 after playtest so
   *  a fast marble can't skip past a thin wall in a single tick and so adjacent
   *  walls share a fatter seam. */
  wallHalfThickness: 0.6,
  /** Wall height. Raised 1.2 -> 2.0 so the marble (which gets fast and bouncy)
   *  can't ride up and over the lip on a hard contact. */
  wallHeight: 2.0,
  /** Longitudinal OVERLAP added to EACH end of every wall slab (world units).
   *  Walls are centered on their track slab's midpoint; extending the half-length
   *  by this much makes each wall poke past BOTH ends of its slab and overlap its
   *  neighbours, so the SEALED side walls run continuously the length of the
   *  corridor with no seam at any segment joint for the marble to slip through. */
  wallJointOverlap: 1.2,
  straightLength: { min: 14, max: 26 },
  /** WINDING TURNS — the cursor heading (yaw) steps through a turn so the player
   *  drives a curving track. A turn segment is built as a chain of short straight
   *  slabs, each yawed a little more than the last, so the corridor sweeps smoothly
   *  through the bend (continuous sealed walls follow every slab). */
  turn: {
    /** Number of short slabs a turn is split into (the arc resolution). More slabs
     *  = a smoother sweep + tighter wall seams through the bend. */
    slices: 6,
    /** Length (Z, along the local heading) of EACH slab in a turn. */
    sliceLength: 5.0,
    /** Total yaw swept by a turn, radians. Escalates with difficulty: a gentle
     *  early curve (~0.5 rad ≈ 29°) up to a sharper late turn (~1.15 rad ≈ 66°).
     *  Kept within what the driving model (TURN_RATE = 1.6 rad/s) can carve at the
     *  course's speeds across the slab length. */
    angle: { min: 0.5, max: 1.15 },
    /** BANK — how far the track surface ROLLS into the turn (radians of roll about
     *  the local forward axis), scaled by the turn's sharpness. Banking lets the
     *  carving line hold through the bend. Up to ~0.30 rad (≈ 17°) on the sharpest
     *  turns; gentle curves bank less. */
    bankMax: 0.30,
  },
  /** HILLS — sustained track PITCH sections (real downhills + uphills). A hill
   *  segment is a chain of short slabs pitched up or down so the marble accelerates
   *  on the way down (gravity) and climbs on the way up. */
  hill: {
    /** Number of short slabs a hill is split into. */
    slices: 5,
    /** Length (along the local heading) of EACH slab in a hill. */
    sliceLength: 5.0,
    /** Sustained pitch magnitude, radians. Escalates with difficulty: a gentle
     *  early grade (~0.12 rad ≈ 7°) up to a steeper late one (~0.32 rad ≈ 18°).
     *  Sign (down vs. up) is drawn per hill. */
    pitch: { min: 0.12, max: 0.32 },
  },
  /** Per-segment probabilities for the two new TERRAIN kinds (turn / hill). Both
   *  GROW with difficulty so the course gets twistier and hillier deeper in. Kept
   *  modest so straights still appear between features (rhythm + recovery room). */
  pTurnBase: 0.22,
  pTurnHard: 0.34,
  pHillBase: 0.10,
  pHillHard: 0.18,
  /** DELIBERATE LAUNCH RAMPS (playtest: "ramping should be DELIBERATE").
   *  The general terrain is now FLAT — the main corridor runs level so the marble
   *  stays grounded and controllable, never catching air at a random crest. Air
   *  time is an INTENTIONAL choice: a launch-ramp segment lays a short, distinct
   *  up-wedge in PART of the lane just before a gap, with a long flat landing run
   *  after. The player CHOOSES to hit the wedge (a jump shortcut over the gap) or
   *  lane-shifts around it onto the safe edge strip and threads the gap on foot.
   *  The wedge is tuned (angle + length vs. the marble's speed and gravity) so a
   *  marble that commits clears the gap and lands back on the flat track ahead —
   *  a controllable hop, not a wild fling. */
  launchRamp: {
    /** Flat run-up before the wedge so the marble is grounded and squared up. */
    runUpLength: 6,
    /** Length (Z) of the angled wedge itself. Short — a quick kicker, not a hill.
     *  Long enough (with the pitch) that the marble's contact redirects cleanly
     *  up the incline rather than clipping the lip. */
    wedgeLength: { min: 4.5, max: 6.0 },
    /** Wedge pitch (radians, UP only). Kept moderate so the launch is a clean,
     *  predictable hop: too steep and the marble stalls / over-lofts. ~0.30 early
     *  (a gentle pop) climbing to ~0.42 late (a bigger, committing air). With
     *  g=18 and a grounded speed in the 30s, this clears the gap below and lands
     *  on the flat run-out ahead. */
    pitch: { min: 0.30, max: 0.42 },
    /** Half-width (X) of the wedge — it occupies only the lane CENTER, leaving a
     *  clear strip on BOTH edges so the player can steer around it (lane-shift)
     *  and NOT jump if they'd rather thread the gap/obstacle on the ground. */
    wedgeHalfWidth: 1.5,
    /** Half-length (Z) of the gap punched just past the wedge — the thing the
     *  jump clears. Small enough that the tuned arc lands well beyond it. A skirt
     *  ledge runs along one edge so the grounded (non-jumping) line is possible. */
    gapHalfLength: 1.9,
    /** Flat landing run after the gap. LONG and dead-level so the launched marble
     *  always re-grounds on dedicated flat track before the next feature — the
     *  "flat landing run follows each ramp" requirement. Sized to exceed the
     *  capped jump's max range: with the launch loft capped (physics.ts
     *  MAX_LAUNCH_VY = 11.5 → air time ≈ 2*11.5/18 ≈ 1.28s) and the airborne forward
     *  speed at the course's high end (~55 u/s), the arc spans up to ~70 units; an
     *  80-unit landing run (plus the wedge + gap already cleared ahead of the launch
     *  point) comfortably contains it so the marble lands here, not in a later hole.
     *  Bumped 60 -> 80 alongside the raised loft cap. */
    landingLength: 80,
  },
  /** Base chance a segment is a deliberate LAUNCH-RAMP gambit (up-wedge + gap +
   *  flat landing). Low early (the opening is mostly flat, grounded running) and
   *  GROWS with difficulty so ramp-gambits get more frequent and tighter deeper
   *  in — escalation by distance, as required. */
  pLaunchBase: 0.07,
  /** Launch-ramp probability at full difficulty (late course). Kept moderate so the
   *  turn / hill / gap shares (which also grow with difficulty) leave room for the
   *  five segment kinds to coexist without straights vanishing — the cumulative
   *  kind thresholds stay under 1 at max difficulty (see the segment loop). */
  pLaunchHard: 0.18,
  /** Per-segment probabilities (BASE, early course). Hazards/gaps/movers get
   *  DENSER and boosters slightly more generous as difficulty ramps; see the
   *  difficulty factor in the segment loop.
   *
   *  Hazard base RAISED 0.18 -> 0.40: barriers are now the run's survival threat,
   *  so they have to be a genuine, REACHABLE danger from EARLY on — not clustered
   *  deep where the playtester (who only reached ~Z500) never met one. At 0.40 a
   *  lethal barrier appears within the first couple of post-apron segments, and the
   *  ramp to `pHazardHard` keeps density escalating with distance. A guaranteed
   *  early barrier (see `emitEarlyBarrier`) backstops the very first threat. */
  pHazard: 0.4,
  /** Hazard probability at full difficulty (late course). */
  pHazardHard: 0.72,
  pBooster: 0.22,
  /** Booster probability at full difficulty (more reward to offset the danger). */
  pBoosterHard: 0.42,
  pMover: 0.16,
  /** Mover probability at full difficulty (more moving obstacles late). */
  pMoverHard: 0.34,
  pShortcut: 0.35,
  /** Base chance a given STRAIGHT segment is replaced by a GAP segment (a hole in
   *  the lane to jump/skirt). Gaps get more frequent deeper in. */
  pGapBase: 0.06,
  /** Gap probability at full difficulty. */
  pGapHard: 0.22,
  /** Half-length (Z) of a gap: the missing slab is split into two short edge
   *  slabs leaving a hole of this half-length in the middle. */
  gapHalfLength: 2.2,
  checkpointRadius: 4.0,
  /** SAFE LAUNCH APRON — the forgiving opening stretch the marble launches from.
   *  It is WIDER than the normal base lane and FULLY walled on both sides, with
   *  NO hazards/movers/narrowing, so the player can never fall off at the start.
   *  The marble spawns dead-center (x=0) with comfortable margin to either wall. */
  apronHalfWidth: 6.0,
  /** Length of the apron + run-in before the first seeded feature (one clean,
   *  flat, walled stretch). Long enough that the booster gets the marble fully up
   *  to a committed line before any hazard/gap/mover appears. */
  apronLength: 26,
  /** Forward LAUNCH BOOSTER placed at the spawn, firing along the start heading
   *  (+Z). `apronBoostStrength` is a sustained acceleration (units/s², matching
   *  FORWARD_ACCEL's units) applied while the marble sits on the pad — a firm but
   *  not absurd push off the line so it never stalls or rolls back at the edge.
   *  Lowered 34 -> 14 after live playtest: 34 was a rocket launch that fired the
   *  marble down the apron too hard; 14 is a firm-but-gentle nudge off the line. */
  apronBoostStrength: 14,
  /** How far ahead of spawn the launch pad starts, and its length/half-width.
   *  It straddles the spawn so the impulse engages on tick 0 and keeps pushing
   *  through the early apron. */
  apronBoostHalfLength: 5.0,
} as const;

const SURFACE_NORMAL: SurfaceTuning = { friction: 0.55, restitution: 0.1 };
const SURFACE_ICY: SurfaceTuning = { friction: 0.15, restitution: 0.05 };
const SURFACE_GRIPPY: SurfaceTuning = { friction: 0.85, restitution: 0.08 };
const SURFACE_WALL: SurfaceTuning = { friction: 0.3, restitution: 0.35 };
const SURFACE_MOVER: SurfaceTuning = { friction: 0.6, restitution: 0.1 };

const SURFACES: readonly SurfaceTuning[] = [SURFACE_NORMAL, SURFACE_NORMAL, SURFACE_GRIPPY, SURFACE_ICY];

/** Barrier hazard effects. `slow` is DELIBERATELY ABSENT: it read as a confusing
 *  non-lethal "red" AND each hit scrubbed the marble's velocity (×0.55 in
 *  physics.ts), which kept the marble crawling, starved the launch ramps, and left
 *  the lethal blocks unreachable. Barriers are now a real survival fail state:
 *  predominantly `kill`, with a small `reset_to_checkpoint` share for variety. */
const HAZARD_EFFECTS: readonly HazardEffect[] = ['reset_to_checkpoint', 'kill'];

/** The global physics constants are deterministic and identical client/server. */
const PHYSICS: PhysicsTuning = {
  gravity: [0, -18, 0],
  marbleRadius: 0.5,
  marbleMass: 1,
  marbleFriction: 0.5,
  marbleRestitution: 0.2,
  // Required by the frozen PhysicsTuning contract, but UNUSED by the driving model
  // in physics.ts: steering is a bounded heading TURN_RATE + GRIP carve, not a
  // per-unit steer force.
  steerForce: 26,
  linearDamping: 0.12,
  // Raised 34 -> 48 after live playtest: the marble needs to be faster overall.
  // The progress-scaled cap (PROGRESS_SPEED_GAIN/MAX in physics.ts) still grows
  // on top of this base, so it keeps climbing deeper into the run.
  maxSpeed: 48,
};

interface Cursor {
  /** Center of the lane at the current head, world space. */
  pos: Vec3;
  /** Heading in the XZ plane, radians (0 = +Z, increasing = toward +X). The winding
   *  course STEPS this yaw through turn segments, so the corridor curves; straights
   *  and the apron keep it fixed. All placement helpers resolve their forward / left
   *  vectors from this single yaw, so walls and slabs follow the bend together. */
  yaw: number;
  /** Pitch accumulated, radians (negative = nose-down / downhill, positive = uphill).
   *  Hill segments STEP this; flat features reset it to 0. */
  pitch: number;
}

/** A mutable XYZ triple used while accumulating the world AABB. */
type MutVec3 = [number, number, number];

interface BuildState {
  readonly rng: Rng;
  readonly elements: CourseElement[];
  readonly checkpoints: CourseCheckpoint[];
  nextId: number;
  /** Running AABB of everything placed (mutable while building). */
  readonly boundsMin: MutVec3;
  readonly boundsMax: MutVec3;
  /** Count of lateral barriers emitted so far. Used to ALTERNATE the barrier side
   *  (even -> +X side, odd -> -X side) so consecutive barriers force a genuine
   *  left/right weave instead of (by chance) stacking on the same side. Purely a
   *  function of how many barriers were placed — no RNG draw — so determinism and
   *  draw order are untouched. */
  barrierCount: number;
  /** Count of deliberate LAUNCH RAMPS emitted so far. Used to ALTERNATE which edge
   *  keeps the safe skirt-ledge beside each launch gap, so successive ramp-gambits
   *  demand a different grounded (non-jumping) line. Counter-driven, not RNG, so
   *  determinism is unaffected. */
  rampCount: number;
  /** Count of GAP segments emitted so far. Used to ALTERNATE which edge keeps the
   *  safe ledge on a partial gap, so successive gaps demand different lines.
   *  Counter-driven, not RNG, so determinism is unaffected. */
  gapCount: number;
}

/** Scalar linear interpolation, t expected in [0,1] (not clamped here; callers
 *  pass a clamped difficulty factor). */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Forward unit vector in the XZ plane for a heading. */
function forwardXZ(yaw: number, pitch: number): Vec3 {
  // yaw rotates around Y; pitch tilts down toward -Y as we move forward.
  const cosP = Math.cos(pitch);
  return v3(Math.sin(yaw) * cosP, -Math.sin(pitch), Math.cos(yaw) * cosP);
}

/** Left (−X side) unit vector in the XZ plane for a heading (ignores pitch). */
function leftXZ(yaw: number): Vec3 {
  return v3(-Math.cos(yaw), 0, Math.sin(yaw));
}

/** Yaw-only orientation as a quaternion (rotation about world Y). */
function yawQuat(yaw: number): Quat {
  return quatFromAxisAngle([0, 1, 0], yaw);
}

/** Combined yaw (about Y) then pitch (about local X) as a quaternion. */
function yawPitchQuat(yaw: number, pitch: number): Quat {
  const qy = quatFromAxisAngle([0, 1, 0], yaw);
  // Local X after yaw:
  const localX = v3(Math.cos(yaw), 0, -Math.sin(yaw));
  const qx = quatFromAxisAngle(localX, pitch);
  // Apply yaw first, then pitch (world-space compose: qx * qy).
  return quatMulLocal(qx, qy);
}

/** Combined yaw (about world Y), then pitch (about the post-yaw local X), then
 *  ROLL (about the post-yaw-pitch local forward Z) as a quaternion. Used to BANK a
 *  turn's track surface (roll into the turn) and to pitch a hill. Built by composing
 *  the three local rotations in order so the marble rests on the tilted surface. */
function yawPitchRollQuat(yaw: number, pitch: number, roll: number): Quat {
  const qy = quatFromAxisAngle([0, 1, 0], yaw);
  // Local X after yaw (pitch axis).
  const localX = v3(Math.cos(yaw), 0, -Math.sin(yaw));
  const qx = quatFromAxisAngle(localX, pitch);
  // Forward axis after yaw+pitch (roll axis) = the slab's local +Z.
  const fwd = forwardXZ(yaw, pitch);
  const qz = quatFromAxisAngle(fwd, roll);
  // Compose: yaw first, then pitch, then roll (world-space: qz * qx * qy).
  return quatMulLocal(qz, quatMulLocal(qx, qy));
}

function quatMulLocal(a: Quat, b: Quat): Quat {
  const [ax, ay, az, aw] = a;
  const [bx, by, bz, bw] = b;
  return [
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
    aw * bw - ax * bx - ay * by - az * bz,
  ];
}

function growBounds(st: BuildState, center: Vec3, halfExtents: Vec3): void {
  // Conservative: expand by the element's half-extents (ignores rotation, which
  // is fine for a generous world AABB used for camera framing + kill planes).
  const pad = Math.max(halfExtents[0], halfExtents[1], halfExtents[2]);
  for (let i = 0; i < 3; i++) {
    const lo = center[i] - halfExtents[i] - pad;
    const hi = center[i] + halfExtents[i] + pad;
    if (lo < st.boundsMin[i]) st.boundsMin[i] = lo;
    if (hi > st.boundsMax[i]) st.boundsMax[i] = hi;
  }
}

function pushTrack(
  st: BuildState,
  center: Vec3,
  halfExtents: Vec3,
  rotation: Quat,
  surface: SurfaceTuning,
  kind: 'track' | 'ramp' = 'track',
): void {
  const base = {
    id: st.nextId++,
    transform: { position: center, rotation },
    halfExtents,
    surface,
  } as const;
  const element: TrackElement | RampElement =
    kind === 'ramp' ? { ...base, kind: 'ramp' } : { ...base, kind: 'track' };
  st.elements.push(element);
  growBounds(st, center, halfExtents);
}

function pushWalls(
  st: BuildState,
  center: Vec3,
  yaw: number,
  halfLen: number,
  halfWidth: number,
): void {
  const left = leftXZ(yaw);
  // Extend each wall past BOTH ends of its slab so it overlaps the neighbouring
  // segments' walls — no seam at a joint, and the inside-corner wedge gaps on
  // stepped turns get covered too. The wall stays centered on the slab midpoint,
  // so a symmetric half-length bump overlaps the previous AND next wall equally.
  const wallHalfLen = halfLen + GEN.wallJointOverlap;
  const wallHalf: Vec3 = [GEN.wallHalfThickness, GEN.wallHeight, wallHalfLen];
  const rot = yawQuat(yaw);
  const yLift = GEN.slabHalfThickness + GEN.wallHeight;
  for (const side of [1, -1] as const) {
    const offset = (halfWidth + GEN.wallHalfThickness) * side;
    const wallCenter: Vec3 = [
      center[0] + left[0] * offset,
      center[1] + yLift,
      center[2] + left[2] * offset,
    ];
    st.elements.push({
      id: st.nextId++,
      kind: 'wall',
      transform: { position: wallCenter, rotation: rot },
      halfExtents: wallHalf,
      surface: SURFACE_WALL,
    });
    growBounds(st, wallCenter, wallHalf);
  }
}

/** Walls for an ORIENTED (banked and/or pitched) slab. Identical sealing strategy
 *  to `pushWalls` (overlap the neighbours so the corridor stays continuous through
 *  turns), but the walls share the slab's FULL orientation `rot` (yaw+pitch+roll)
 *  and are offset along the slab's LOCAL left and up axes (the quat-rotated unit
 *  axes) so they ride the banked/pitched surface edges and stay sealed through the
 *  bend. Used by the turn and hill emitters. `rot` is the slab's track rotation. */
function pushWallsOriented(
  st: BuildState,
  center: Vec3,
  rot: Quat,
  halfLen: number,
  halfWidth: number,
): void {
  // Slab-local axes in world space: left = +X' (lane across), up = +Y'.
  const localLeft = quatRotate(rot, [1, 0, 0]);
  const localUp = quatRotate(rot, [0, 1, 0]);
  const wallHalfLen = halfLen + GEN.wallJointOverlap;
  const wallHalf: Vec3 = [GEN.wallHalfThickness, GEN.wallHeight, wallHalfLen];
  const yLift = GEN.slabHalfThickness + GEN.wallHeight;
  for (const side of [1, -1] as const) {
    const offset = (halfWidth + GEN.wallHalfThickness) * side;
    const wallCenter: Vec3 = [
      center[0] + localLeft[0] * offset + localUp[0] * yLift,
      center[1] + localLeft[1] * offset + localUp[1] * yLift,
      center[2] + localLeft[2] * offset + localUp[2] * yLift,
    ];
    st.elements.push({
      id: st.nextId++,
      kind: 'wall',
      transform: { position: wallCenter, rotation: rot },
      halfExtents: wallHalf,
      surface: SURFACE_WALL,
    });
    growBounds(st, wallCenter, wallHalf);
  }
}

function pushCheckpoint(st: BuildState, position: Vec3, isFinish: boolean): void {
  st.checkpoints.push({
    index: st.checkpoints.length,
    position: [position[0], position[1] + 1, position[2]],
    radius: GEN.checkpointRadius,
    isFinish,
  });
}

/** A STRAIGHT, LEVEL run of track along the current heading. Advances the cursor
 *  forward `length` units at the cursor's current YAW (so a straight after a turn
 *  keeps the new heading) with NO pitch — straights are the flat recovery sections
 *  between turns and hills. `laneHalf` is the current (difficulty-narrowed) lane
 *  half-width. */
function emitStraight(
  st: BuildState,
  cur: Cursor,
  length: number,
  laneHalf: number,
): void {
  const fwd = forwardXZ(cur.yaw, 0);
  const halfLen = length / 2;
  const mid: Vec3 = [
    cur.pos[0] + fwd[0] * halfLen,
    cur.pos[1] + fwd[1] * halfLen,
    cur.pos[2] + fwd[2] * halfLen,
  ];
  const surface = st.rng.pick(SURFACES);
  pushTrack(
    st,
    mid,
    [laneHalf, GEN.slabHalfThickness, halfLen],
    yawQuat(cur.yaw),
    surface,
    'track',
  );
  pushWalls(st, mid, cur.yaw, halfLen, laneHalf);
  // Advance cursor to the far end (heading unchanged).
  cur.pos = [
    cur.pos[0] + fwd[0] * length,
    cur.pos[1] + fwd[1] * length,
    cur.pos[2] + fwd[2] * length,
  ];
  cur.pitch = 0;
}

/** A WINDING TURN — the heart of the driveable course. Steps the cursor's YAW
 *  through a bend built as a chain of `GEN.turn.slices` short slabs, each advanced
 *  `sliceLength` along the (updated) heading and yawed by an equal share of the
 *  total turn `angle`. The track surface is BANKED (rolled into the turn) so the
 *  carving line holds: the bank rolls up to `bankMax * (angle / maxAngle)` —
 *  sharper turns bank more. Continuous sealed walls follow every slab on both edges
 *  (via `pushWallsOriented`), so a mis-steer bounces off a wall instead of falling
 *  off. `dir` is +1 (turn toward +X) or −1 (toward −X); `angle` is the total sweep.
 *
 *  Determinism: `emitTurn` makes NO RNG draws of its own (direction + angle are
 *  passed in, derived from a single caller draw + the difficulty factor), so draw
 *  order is unchanged. `laneHalf` is the current narrowed lane half-width. */
function emitTurn(
  st: BuildState,
  cur: Cursor,
  laneHalf: number,
  dir: -1 | 1,
  angle: number,
): void {
  const slices = GEN.turn.slices;
  const sliceLen = GEN.turn.sliceLength;
  const sliceHalf = sliceLen / 2;
  const yawStep = (angle * dir) / slices;
  // Bank scales with the turn's sharpness relative to the max turn angle, and rolls
  // INTO the turn: a +X turn (dir +1) rolls the surface so its +X edge dips. The
  // roll sign is chosen so the banked surface cups the marble through the bend.
  const bank = -dir * GEN.turn.bankMax * (angle / GEN.turn.angle.max);
  const surface = st.rng.pick(SURFACES);
  for (let i = 0; i < slices; i++) {
    // Yaw the heading by this slice's share BEFORE laying the slab, so the slab is
    // already aligned with the new heading (a smooth swept arc).
    cur.yaw += yawStep;
    const fwd = forwardXZ(cur.yaw, 0);
    const mid: Vec3 = [
      cur.pos[0] + fwd[0] * sliceHalf,
      cur.pos[1],
      cur.pos[2] + fwd[2] * sliceHalf,
    ];
    const rot = yawPitchRollQuat(cur.yaw, 0, bank);
    pushTrack(st, mid, [laneHalf, GEN.slabHalfThickness, sliceHalf], rot, surface, 'track');
    pushWallsOriented(st, mid, rot, sliceHalf, laneHalf);
    cur.pos = [
      cur.pos[0] + fwd[0] * sliceLen,
      cur.pos[1],
      cur.pos[2] + fwd[2] * sliceLen,
    ];
  }
  cur.pitch = 0;
}

/** A HILL — a sustained PITCH section (a real downhill or uphill). Built as a chain
 *  of `GEN.hill.slices` short slabs pitched by `pitch` (negative = downhill, the
 *  marble accelerates under gravity; positive = uphill). Each slab is laid along the
 *  pitched forward so the cursor descends/climbs in world Y. Sealed walls follow
 *  every slab. Makes NO RNG draws of its own (pitch passed in from one caller draw),
 *  so determinism holds. `laneHalf` is the current narrowed lane half-width. */
function emitHill(
  st: BuildState,
  cur: Cursor,
  laneHalf: number,
  pitch: number,
): void {
  const slices = GEN.hill.slices;
  const sliceLen = GEN.hill.sliceLength;
  const sliceHalf = sliceLen / 2;
  const surface = st.rng.pick(SURFACES);
  const rot = yawPitchRollQuat(cur.yaw, pitch, 0);
  for (let i = 0; i < slices; i++) {
    const fwd = forwardXZ(cur.yaw, pitch);
    const mid: Vec3 = [
      cur.pos[0] + fwd[0] * sliceHalf,
      cur.pos[1] + fwd[1] * sliceHalf,
      cur.pos[2] + fwd[2] * sliceHalf,
    ];
    pushTrack(st, mid, [laneHalf, GEN.slabHalfThickness, sliceHalf], rot, surface, 'track');
    pushWallsOriented(st, mid, rot, sliceHalf, laneHalf);
    cur.pos = [
      cur.pos[0] + fwd[0] * sliceLen,
      cur.pos[1] + fwd[1] * sliceLen,
      cur.pos[2] + fwd[2] * sliceLen,
    ];
  }
  cur.pitch = 0;
}

/** A GAP segment: a straight run of track with a HOLE punched in the lane (a
 *  hazard to dodge by skirting to the edge). The slab is split into a lead-in and
 *  a run-out leaving a hole of `2 * GEN.gapHalfLength` between them.
 *
 *  VARIED PLACEMENT: `ledgeSide` controls how the hole sits across the lane:
 *    - 0  -> a FULL-WIDTH hole (the classic: nothing to stand on; the player must
 *            already be past it / carried over by speed).
 *    - ±1 -> a PARTIAL hole pushed to one side, leaving a narrow SAFE LEDGE strip
 *            running along the OTHER edge that the player can thread along. The
 *            sign picks which edge keeps the ledge, so successive gaps demand
 *            different lines.
 *  Walls run continuously past the gap on both edges so the corridor stays sealed.
 *  `laneHalf` is the current (difficulty-narrowed) lane half-width. */
function emitGap(
  st: BuildState,
  cur: Cursor,
  length: number,
  laneHalf: number,
  ledgeSide: -1 | 0 | 1,
): void {
  const fwd = forwardXZ(cur.yaw, 0);
  const left = leftXZ(cur.yaw);
  const gapHalf = Math.min(GEN.gapHalfLength, length / 2 - 1);
  // Solid track on either side of the hole (along Z).
  const sideLen = Math.max(1, length / 2 - gapHalf);
  const sideHalf = sideLen / 2;
  // Lead-in slab (nearest the cursor) — always full lane width.
  const inMid: Vec3 = [
    cur.pos[0] + fwd[0] * sideHalf,
    cur.pos[1] + fwd[1] * sideHalf,
    cur.pos[2] + fwd[2] * sideHalf,
  ];
  pushTrack(st, inMid, [laneHalf, GEN.slabHalfThickness, sideHalf], yawQuat(cur.yaw), SURFACE_NORMAL);
  // Run-out slab (far side of the hole) — always full lane width.
  const outStart = sideLen + 2 * gapHalf;
  const outMid: Vec3 = [
    cur.pos[0] + fwd[0] * (outStart + sideHalf),
    cur.pos[1] + fwd[1] * (outStart + sideHalf),
    cur.pos[2] + fwd[2] * (outStart + sideHalf),
  ];
  pushTrack(st, outMid, [laneHalf, GEN.slabHalfThickness, sideHalf], yawQuat(cur.yaw), SURFACE_NORMAL);
  // PARTIAL gap: lay a narrow safe LEDGE strip spanning the hole along one edge so
  // the player can thread it on that side. (ledgeSide === 0 leaves a full-width
  // hole — no ledge.)
  if (ledgeSide !== 0) {
    const ledgeHalfW = Math.min(1.1, laneHalf * 0.4);
    // Offset so the ledge hugs the chosen edge: from the lane center, push by
    // (laneHalf - ledgeHalfW) toward `ledgeSide`.
    const ledgeOffset = (laneHalf - ledgeHalfW) * ledgeSide;
    const ledgeCenter: Vec3 = [
      cur.pos[0] + fwd[0] * (sideLen + gapHalf) + left[0] * ledgeOffset,
      cur.pos[1] + fwd[1] * (sideLen + gapHalf),
      cur.pos[2] + fwd[2] * (sideLen + gapHalf) + left[2] * ledgeOffset,
    ];
    pushTrack(
      st,
      ledgeCenter,
      [ledgeHalfW, GEN.slabHalfThickness, gapHalf],
      yawQuat(cur.yaw),
      SURFACE_ICY,
    );
  }
  // Continuous sealed walls spanning the WHOLE segment (including over the hole),
  // centered on the segment midpoint so they overlap the neighbouring walls.
  const segHalf = length / 2;
  const segMid: Vec3 = [
    cur.pos[0] + fwd[0] * segHalf,
    cur.pos[1] + fwd[1] * segHalf,
    cur.pos[2] + fwd[2] * segHalf,
  ];
  pushWalls(st, segMid, cur.yaw, segHalf, laneHalf);
  cur.pos = [
    cur.pos[0] + fwd[0] * length,
    cur.pos[1] + fwd[1] * length,
    cur.pos[2] + fwd[2] * length,
  ];
  cur.pitch = 0;
}

/**
 * Emit a DELIBERATE LAUNCH-RAMP segment — the only pitched geometry in the
 * otherwise-flat corridor. Layout, head-to-tail along +Z:
 *
 *   [ flat run-up ][ UP-WEDGE + flat side strips ][ GAP (skirt ledge) ][ flat landing run ]
 *
 *  - The run-up is level so the marble arrives grounded and squared up.
 *  - The WEDGE is a short up-ramp occupying only the lane CENTER
 *    (`wedgeHalfWidth`). Its near (-Z) edge sits flush with the flat track and its
 *    far (+Z) edge is raised, so a marble that runs over it is redirected up and
 *    launched. FLAT side strips flank it on BOTH edges at floor height, so a
 *    player who lane-shifts to either side rolls past the wedge WITHOUT jumping.
 *  - A GAP is punched right past the wedge — the thing the jump clears. A narrow
 *    skirt LEDGE runs along one edge (side alternates with the launch-ramp counter)
 *    so the grounded (non-jumping) line can thread the gap on foot.
 *  - A long FLAT landing run follows so the launched marble re-grounds and settles.
 *
 *  TUNING (deterministic): the wedge pitch and length are drawn from the launchRamp
 *  ranges, escalating with `diff` (gentler/shorter early, steeper deeper in). With
 *  the global gravity (18) and a grounded forward speed in the 30s, the resulting
 *  ballistic arc clears the gap below and lands on the flat run-out ahead — a
 *  controllable hop, not a wild fling. Sealed side walls span the whole segment.
 *  All randomness is from `makeRng` in a fixed draw order.
 */
function emitLaunchRamp(st: BuildState, cur: Cursor, laneHalf: number, diff: number): void {
  const L = GEN.launchRamp;
  // Fixed draw order: ONE jitter draw, applied to BOTH the wedge length and pitch
  // so the segment makes exactly one RNG draw of its own (the sub-emitters below
  // make their usual draws after). `diff` biases the base magnitudes within their
  // ranges (gentler/shorter early, steeper deeper in); the jitter adds a little
  // variety without changing the draw count or order, so determinism holds.
  const jitter = st.rng.next(); // [0,1)
  const wedgeLen = lerp(L.wedgeLength.min, L.wedgeLength.max, diff);
  const pitchMag = lerp(L.pitch.min, L.pitch.max, diff) + (jitter - 0.5) * 0.04;

  const fwd = forwardXZ(cur.yaw, 0);
  const left = leftXZ(cur.yaw);
  const wedgeHalfW = Math.min(L.wedgeHalfWidth, laneHalf * 0.6);
  const wedgeRise = Math.sin(pitchMag) * wedgeLen; // far-edge lift vs. near edge

  // --- 1) Flat run-up (full lane width, level) ---
  emitStraight(st, cur, L.runUpLength, laneHalf);

  // --- 2) Wedge + flanking flat side strips ---
  // The wedge is centered on the lane; its near edge is flush with the run-up
  // floor, so it is lifted by half its rise. yawPitchQuat with a NEGATIVE pitch
  // tilts the far (+Z) edge UP (an up-ramp the marble climbs and launches off).
  const wedgeHalfLen = wedgeLen / 2;
  const wedgeMid: Vec3 = [
    cur.pos[0] + fwd[0] * wedgeHalfLen,
    cur.pos[1] + wedgeRise / 2,
    cur.pos[2] + fwd[2] * wedgeHalfLen,
  ];
  pushTrack(
    st,
    wedgeMid,
    [wedgeHalfW, GEN.slabHalfThickness, wedgeHalfLen],
    yawPitchQuat(cur.yaw, -pitchMag),
    SURFACE_GRIPPY,
    'ramp',
  );
  // Flat side strips beside the wedge (both edges) at floor height, so steering
  // around the wedge keeps the marble grounded and level. Each strip spans from
  // the wedge edge out to the lane edge.
  const stripHalfW = Math.max(0.2, (laneHalf - wedgeHalfW) / 2);
  if (laneHalf - wedgeHalfW > 0.4) {
    for (const side of [1, -1] as const) {
      const stripOffset = (wedgeHalfW + stripHalfW) * side;
      const stripMid: Vec3 = [
        cur.pos[0] + fwd[0] * wedgeHalfLen + left[0] * stripOffset,
        cur.pos[1],
        cur.pos[2] + fwd[2] * wedgeHalfLen + left[2] * stripOffset,
      ];
      pushTrack(
        st,
        stripMid,
        [stripHalfW, GEN.slabHalfThickness, wedgeHalfLen],
        yawQuat(cur.yaw),
        SURFACE_NORMAL,
      );
    }
  }
  // Walls span the wedge stretch (centered on its midpoint, overlapping neighbours).
  pushWalls(st, wedgeMid, cur.yaw, wedgeHalfLen, laneHalf);
  // Advance the cursor past the wedge, back at FLOOR height (the launch is airborne;
  // the geometry ahead — gap + landing — stays level so the marble re-grounds flat).
  cur.pos = [
    cur.pos[0] + fwd[0] * wedgeLen,
    cur.pos[1],
    cur.pos[2] + fwd[2] * wedgeLen,
  ];
  cur.pitch = 0;

  // --- 3) GAP just past the wedge — the obstacle the jump clears ---
  // Alternate the skirt-ledge edge with the launch-ramp counter so successive
  // gambits demand a different grounded line. Counter-driven (no RNG draw).
  const ledgeSide: -1 | 1 = st.rampCount % 2 === 0 ? 1 : -1;
  st.rampCount += 1;
  const gapHalf = L.gapHalfLength;
  emitGapSpan(st, cur, gapHalf, laneHalf, ledgeSide);

  // --- 4) Long flat landing run so the marble re-grounds and settles ---
  emitStraight(st, cur, L.landingLength, laneHalf);
}

/** A bare GAP of half-length `gapHalf` at the cursor (no run-in/run-out slabs):
 *  a hole punched directly into the lane with a skirt ledge along one edge and
 *  sealed walls spanning it. Used by `emitLaunchRamp` where the wedge IS the
 *  lead-in. Advances the cursor past the hole. */
function emitGapSpan(
  st: BuildState,
  cur: Cursor,
  gapHalf: number,
  laneHalf: number,
  ledgeSide: -1 | 1,
): void {
  const fwd = forwardXZ(cur.yaw, 0);
  const left = leftXZ(cur.yaw);
  const span = 2 * gapHalf;
  // Skirt ledge spanning the hole along the chosen edge (the grounded line).
  const ledgeHalfW = Math.min(1.1, laneHalf * 0.4);
  const ledgeOffset = (laneHalf - ledgeHalfW) * ledgeSide;
  const ledgeCenter: Vec3 = [
    cur.pos[0] + fwd[0] * gapHalf + left[0] * ledgeOffset,
    cur.pos[1],
    cur.pos[2] + fwd[2] * gapHalf + left[2] * ledgeOffset,
  ];
  pushTrack(
    st,
    ledgeCenter,
    [ledgeHalfW, GEN.slabHalfThickness, gapHalf],
    yawQuat(cur.yaw),
    SURFACE_ICY,
  );
  // Sealed walls spanning the hole.
  const segMid: Vec3 = [
    cur.pos[0] + fwd[0] * gapHalf,
    cur.pos[1],
    cur.pos[2] + fwd[2] * gapHalf,
  ];
  pushWalls(st, segMid, cur.yaw, gapHalf, laneHalf);
  cur.pos = [
    cur.pos[0] + fwd[0] * span,
    cur.pos[1] + fwd[1] * span,
    cur.pos[2] + fwd[2] * span,
  ];
  cur.pitch = 0;
}

/** Place a hazard trigger volume near the lane center at the cursor.
 *  `pThreshold` is the difficulty-scaled spawn chance and `laneHalf` is the
 *  current (narrowed) lane half-width so hazards still sit on the lane. The gate
 *  number is ALWAYS drawn first so draw order — and thus determinism — does not
 *  depend on the threshold. */
function maybeHazard(
  st: BuildState,
  cur: Cursor,
  pThreshold: number,
  laneHalf: number,
  diff: number,
  force = false,
): void {
  // Draw gate FIRST so draw order (determinism) is independent of the threshold.
  // ALL four draws happen unconditionally and in the SAME order whether or not the
  // barrier is `force`d, so forcing an early barrier never shifts the RNG stream.
  const gate = st.rng.next();
  const sideRoll = st.rng.next();
  // ALWAYS draw the effect (consumes one RNG number) so draw order is identical in
  // the forced and unforced paths; `force` only OVERRIDES the drawn value to `kill`.
  const drawnEffect = barrierHazard(st.rng);
  const effect: HazardEffect = force ? 'kill' : drawnEffect;
  const widthRoll = st.rng.next();
  // `force` guarantees a barrier here regardless of the gate (used for the FIRST,
  // reachable lethal barrier just after the apron). The effect-draw above is kept
  // even when forced so draw order is byte-for-byte identical to the unforced path.
  if (!force && gate >= pThreshold) return;
  const left = leftXZ(cur.yaw);
  // A LATERAL BARRIER to DODGE: a wide hazard slab offset to one side, leaving a
  // clear dodge lane on the OTHER side. It spans MORE of the lane (and leaves a
  // tighter dodge gap) the deeper in you are, so the player must shift across the
  // corridor to thread it. The hazard's X half-width is a fraction of the lane
  // that grows with difficulty; it is pushed toward the chosen wall so the gap is
  // on the far side.
  //
  // ALTERNATING SIDE: the side is driven by the running barrier counter (even ->
  // +X, odd -> -X) so consecutive barriers force a genuine left/right weave rather
  // than (by chance) stacking on the same side. `sideRoll` is still drawn (draw
  // order is unchanged) and is used to occasionally (~25%) flip the alternation so
  // the rhythm isn't perfectly mechanical. Counter + roll are both deterministic.
  const alt = st.barrierCount % 2 === 0 ? 1 : -1;
  const side = sideRoll < 0.25 ? -alt : alt;
  st.barrierCount += 1;
  // Fraction of the lane the barrier blocks: ~0.5 early -> ~0.85 late, jittered.
  const blockFrac = lerp(0.45, 0.8, diff) + widthRoll * 0.1;
  const halfX = laneHalf * blockFrac;
  // Center the barrier so its inner edge leaves a dodge gap against the far wall:
  // push it toward `side` by (laneHalf - halfX).
  const offset = (laneHalf - halfX) * side;
  const center: Vec3 = [
    cur.pos[0] + left[0] * offset,
    // Tall barrier (as tall as the side walls) so a ramping/airborne marble can't
    // fly OVER a kill block — you must dodge laterally through the gap.
    cur.pos[1] + GEN.slabHalfThickness + 2.0,
    cur.pos[2] + left[2] * offset,
  ];
  const half: Vec3 = [halfX, 2.0, 0.9];
  st.elements.push({
    id: st.nextId++,
    kind: 'hazard',
    transform: { position: center, rotation: yawQuat(cur.yaw) },
    halfExtents: half,
    effect,
  });
  growBounds(st, center, half);
}

/** LATERAL-BARRIER effect bias. The barrier (red block) IS the survival fail
 *  state the player must dodge, so hitting one ENDS the run by default: ~80% kill,
 *  ~20% reset_to_checkpoint, and ZERO slow. `slow` was removed outright — it read
 *  as a toothless non-lethal "red" AND its ×0.55 velocity scrub was the very thing
 *  crippling the marble's speed (keeping it crawling, starving the launch ramps,
 *  and leaving the lethal blocks unreachable). A single `rng.next()` draw, so draw
 *  order — and thus determinism — is unchanged from the old `weightedHazard`. */
function barrierHazard(rng: Rng): HazardEffect {
  const r = rng.next();
  if (r < 0.2) return HAZARD_EFFECTS[0]; // reset_to_checkpoint (small share)
  return HAZARD_EFFECTS[1]; // kill (hitting a red barrier ENDS the run)
}

/** Place a forward booster pad. `pThreshold` is the difficulty-scaled spawn
 *  chance and `laneHalf` the current narrowed lane half-width. Gate number drawn
 *  first to keep draw order (and determinism) threshold-independent. */
function maybeBooster(st: BuildState, cur: Cursor, pThreshold: number, laneHalf: number): void {
  if (st.rng.next() >= pThreshold) return;
  const fwd = forwardXZ(cur.yaw, 0);
  const strength = st.rng.range(8, 16);
  const center: Vec3 = [cur.pos[0], cur.pos[1] + GEN.slabHalfThickness + 0.3, cur.pos[2]];
  const half: Vec3 = [laneHalf * 0.5, 0.3, 1.6];
  st.elements.push({
    id: st.nextId++,
    kind: 'booster',
    transform: { position: center, rotation: yawQuat(cur.yaw) },
    halfExtents: half,
    impulse: [fwd[0] * strength, 0, fwd[2] * strength],
  });
  growBounds(st, center, half);
}

/** Place a kinematic SIDE-TO-SIDE SWEEPER crossing the lane (the marble must time
 *  its lateral shift to slip past). `pThreshold` is the difficulty-scaled spawn
 *  chance and `laneHalf` the current narrowed lane half-width; `diff` makes the
 *  sweep faster (shorter period) deeper in. Gate number drawn first to keep
 *  determinism threshold-independent. */
function maybeMover(
  st: BuildState,
  cur: Cursor,
  pThreshold: number,
  laneHalf: number,
  diff: number,
): void {
  // Draw ALL randomness up front (threshold-independent draw order), then decide.
  // Ranges WIDENED for varied sweeper timings: the amplitude spans from a short
  // jab up to nearly the full lane, and the raw period spreads wide (slow, lazy
  // sweeps through to fast, snappy ones) so consecutive sweepers don't feel like
  // the same metronome.
  const gate = st.rng.next();
  const ampRoll = st.rng.range(1.2, laneHalf * 1.05);
  const periodRoll = st.rng.range(70, 260);
  const phaseRoll = st.rng.next();
  if (gate >= pThreshold) return;
  const left = leftXZ(cur.yaw);
  const center: Vec3 = [cur.pos[0], cur.pos[1], cur.pos[2]];
  const half: Vec3 = [1.4, GEN.slabHalfThickness, 1.8];
  const amplitude = ampRoll;
  // Sweep faster late: scale the raw period down toward ~0.45x at full difficulty
  // (was 0.55x) so late sweepers are genuinely quick. Floor kept so it never
  // becomes a sub-tick blur.
  const periodTicks = Math.max(36, Math.round(periodRoll * lerp(1, 0.45, diff)));
  const phaseTicks = Math.round(phaseRoll * periodTicks);
  st.elements.push({
    id: st.nextId++,
    kind: 'mover',
    transform: { position: center, rotation: yawQuat(cur.yaw) },
    halfExtents: half,
    surface: SURFACE_MOVER,
    motion: {
      axis: [left[0], 0, left[2]],
      amplitude,
      periodTicks,
      phaseTicks,
    },
  });
  growBounds(st, center, half);
}

/**
 * Spawn a parallel SHORTCUT lane: a narrow strip offset to one side that runs
 * a bit shorter than the main path and rejoins downstream. It usually carries a
 * hazard (the "risk") but saves distance/time (the "reward"). It is geometry the
 * player MAY take; checkpoints still live on the main line so the shortcut can't
 * skip required gates — it only trims the racing line.
 */
function maybeShortcut(st: BuildState, cur: Cursor, length: number, laneHalf: number): void {
  if (st.rng.next() >= GEN.pShortcut) return;
  const left = leftXZ(cur.yaw);
  const fwd = forwardXZ(cur.yaw, 0);
  const side = st.rng.next() < 0.5 ? 1 : -1;
  const lateral = (laneHalf + 2.4) * side;
  const shortHalfWidth = 1.6;
  const halfLen = (length * 0.7) / 2;
  const base: Vec3 = [
    cur.pos[0] + left[0] * lateral,
    cur.pos[1] - 0.2,
    cur.pos[2] + left[2] * lateral,
  ];
  const mid: Vec3 = [
    base[0] + fwd[0] * halfLen,
    base[1] + fwd[1] * halfLen,
    base[2] + fwd[2] * halfLen,
  ];
  pushTrack(st, mid, [shortHalfWidth, GEN.slabHalfThickness, halfLen], yawQuat(cur.yaw), SURFACE_ICY);
  // Outer wall only (inner side open so the player can dive in/out).
  const outerOffset = (shortHalfWidth + GEN.wallHalfThickness) * side;
  const wallCenter: Vec3 = [
    mid[0] + left[0] * outerOffset,
    mid[1] + GEN.slabHalfThickness + GEN.wallHeight,
    mid[2] + left[2] * outerOffset,
  ];
  const wallHalf: Vec3 = [GEN.wallHalfThickness, GEN.wallHeight, halfLen];
  st.elements.push({
    id: st.nextId++,
    kind: 'wall',
    transform: { position: wallCenter, rotation: yawQuat(cur.yaw) },
    halfExtents: wallHalf,
    surface: SURFACE_WALL,
  });
  growBounds(st, wallCenter, wallHalf);
  // The risk: a hazard guarding the shortcut's mouth. It is a
  // `reset_to_checkpoint` (NOT `slow`): the shortcut's penalty has to bite to be a
  // real risk/reward, but `slow` was removed everywhere — its ×0.55 velocity scrub
  // crippled the marble's speed. Reset is the non-kill penalty that keeps the
  // shortcut a gamble without scrubbing momentum across the whole field.
  const hazardCenter: Vec3 = [base[0], base[1] + GEN.slabHalfThickness + 0.6, base[2]];
  const hHalf: Vec3 = [shortHalfWidth * 0.8, 0.6, 0.9];
  st.elements.push({
    id: st.nextId++,
    kind: 'hazard',
    transform: { position: hazardCenter, rotation: yawQuat(cur.yaw) },
    halfExtents: hHalf,
    effect: 'reset_to_checkpoint',
  });
  growBounds(st, hazardCenter, hHalf);
}

/**
 * Deterministically build the day's course from its numeric seed.
 * The same (seed, dateKey) always yields a structurally identical CourseSpec.
 */
export function generateCourse(seed: number, dateKey: string): CourseSpec {
  const rng = makeRng(seed);

  const spawnPos: Vec3 = [0, PHYSICS.marbleRadius + GEN.slabHalfThickness + 0.05, 0];
  const st: BuildState = {
    rng,
    elements: [],
    checkpoints: [],
    nextId: 0,
    boundsMin: [spawnPos[0], spawnPos[1], spawnPos[2]],
    boundsMax: [spawnPos[0], spawnPos[1], spawnPos[2]],
    barrierCount: 0,
    rampCount: 0,
    gapCount: 0,
  };

  // SAFE LAUNCH APRON — a WIDE, FULLY-WALLED, flat opening stretch the marble
  // launches from. It is wider than the normal base lane and carries NO hazards,
  // movers, narrowing, or turns, so the player cannot fall off at the start. The
  // marble spawns dead-center (x=0) with comfortable margin (apronHalfWidth) to
  // either wall. `emitStraight` walls BOTH edges with no gaps; we draw it before
  // the seeded loop using a FIXED width, so it consumes RNG only via the surface
  // pick (kept first in draw order, exactly as a normal straight would).
  const cur: Cursor = { pos: [0, 0, 0], yaw: 0, pitch: 0 };
  emitStraight(st, cur, GEN.apronLength, GEN.apronHalfWidth);

  // LAUNCH BOOSTER — a forward booster straddling the spawn that fires along the
  // start heading (+Z). It engages on tick 0 (the spawn sits inside the pad) and
  // keeps pushing through the opening apron, so the marble gets going off the line
  // and never stalls or rolls back at the edge. Fixed placement: NO RNG draws, so
  // determinism draw order is untouched.
  const launchPadCenter: Vec3 = [0, GEN.slabHalfThickness + 0.3, GEN.apronBoostHalfLength - 1.0];
  st.elements.push({
    id: st.nextId++,
    kind: 'booster',
    transform: { position: launchPadCenter, rotation: yawQuat(0) },
    halfExtents: [GEN.apronHalfWidth * 0.85, 0.3, GEN.apronBoostHalfLength],
    impulse: [0, 0, GEN.apronBoostStrength],
  });
  growBounds(st, launchPadCenter, [GEN.apronHalfWidth * 0.85, 0.3, GEN.apronBoostHalfLength]);

  const segCount =
    GEN.segmentCount.min + rng.int(GEN.segmentCount.max - GEN.segmentCount.min + 1);

  // Place an early checkpoint right after the apron.
  pushCheckpoint(st, cur.pos, false);

  // GUARANTEED FIRST LETHAL BARRIER — reachable from the very start.
  // The playtester only ever reached ~Z500, yet the first kill block used to sit at
  // Z≈1662 (deep, unreachable). Here we backstop the survival threat: a short flat
  // lead-in (fixed length, so it draws RNG only via its surface pick exactly like a
  // normal straight) gets the boosted marble moving, then we FORCE a kill barrier on
  // it. With the apron ending at Z=26 and this 14-unit lead-in, the first red block
  // the player meets is at Z≈40 — well inside the reachable opening and the
  // "first 120–250 units" window — so "red kills" is true immediately, not 1600
  // units away. The loop's raised `pHazard` then keeps lethal barriers coming
  // throughout, with density escalating to `pHazardHard` deeper in.
  emitStraight(st, cur, 14, GEN.laneHalfWidth);
  maybeHazard(st, cur, 1, GEN.laneHalfWidth, 0, /* force */ true);

  for (let i = 0; i < segCount; i++) {
    // Difficulty factor in [0,1] derived from segment index: early sections are
    // forgiving, later sections hard. Determinism is unaffected — `diff` is a
    // pure function of the loop index, never of any RNG draw, and every branch
    // still pulls the SAME numbers in the SAME order regardless of `diff`. We
    // only use `diff` to (a) narrow the lane, (b) bias the segment-kind/spawn
    // thresholds the single existing draws are compared against, and (c) widen
    // the turn-angle range — none of which changes draw order or count.
    const diff = segCount > 1 ? i / (segCount - 1) : 0;

    // Lane narrows with progress (forgiving -> tight) and turns / hills / hazards /
    // gaps / movers get denser & sharper. The course WINDS: turn segments step the
    // heading and hill segments pitch it, so the player actively STEERS through a
    // curving, rolling track. `diff` only biases thresholds/widths the existing
    // draws are compared against — it never changes draw order or count, so
    // determinism is unaffected.
    const laneHalf = lerp(GEN.laneHalfWidth, GEN.laneHalfWidthMin, diff);
    const pHazard = lerp(GEN.pHazard, GEN.pHazardHard, diff);
    const pBooster = lerp(GEN.pBooster, GEN.pBoosterHard, diff);
    const pMover = lerp(GEN.pMover, GEN.pMoverHard, diff);
    const pGap = lerp(GEN.pGapBase, GEN.pGapHard, diff);
    const pLaunch = lerp(GEN.pLaunchBase, GEN.pLaunchHard, diff);
    const pTurn = lerp(GEN.pTurnBase, GEN.pTurnHard, diff);
    const pHill = lerp(GEN.pHillBase, GEN.pHillHard, diff);

    // Choose a segment kind from a single `roll` with CUMULATIVE thresholds (one
    // draw, so draw order is unchanged):
    //   STRAIGHT (flat recovery) | TURN (banked bend) | HILL (down/up grade) |
    //   GAP (hole to dodge)      | LAUNCH-RAMP (jump gambit).
    // The turn / hill / gap / launch shares all GROW with `diff`, so deeper sections
    // are twistier, hillier, and denser. Each branch makes its OWN fixed extra draws
    // AFTER the kind roll; the branch taken depends only on prior draws, so the
    // stream stays fully deterministic. `diff` only biases the thresholds the single
    // roll is compared against — never the draw order or count.
    const roll = rng.next();
    const pStraight = Math.max(0, 1 - pTurn - pHill - pGap - pLaunch);
    if (roll < pStraight) {
      const len = rng.range(GEN.straightLength.min, GEN.straightLength.max);
      emitStraight(st, cur, len, laneHalf);
      maybeBooster(st, cur, pBooster, laneHalf);
      maybeMover(st, cur, pMover, laneHalf, diff);
      maybeHazard(st, cur, pHazard, laneHalf, diff);
      maybeShortcut(st, cur, len, laneHalf);
    } else if (roll < pStraight + pTurn) {
      // WINDING TURN: a banked bend the player carves through. Two extra draws —
      // one picks the turn DIRECTION (left/right), one jitters the sweep ANGLE
      // within the difficulty-scaled range — then sealed-walled slabs sweep the
      // heading around. A hazard may sit on the exit so the turn isn't a free ride.
      const dirRoll = rng.next();
      const angRoll = rng.next();
      const dir: -1 | 1 = dirRoll < 0.5 ? 1 : -1;
      const angle = lerp(GEN.turn.angle.min, GEN.turn.angle.max, diff * 0.7 + angRoll * 0.3);
      emitTurn(st, cur, laneHalf, dir, angle);
      maybeHazard(st, cur, pHazard, laneHalf, diff);
    } else if (roll < pStraight + pTurn + pHill) {
      // HILL: a sustained down/up grade. One extra draw picks the pitch MAGNITUDE in
      // the difficulty-scaled range; the SIGN alternates with the segment index so
      // downhills and uphills trade off (no RNG for the sign — index-driven, so
      // determinism holds). A booster on a downhill rewards the committed line.
      const pitchRoll = rng.next();
      const pitchMag = lerp(GEN.hill.pitch.min, GEN.hill.pitch.max, diff * 0.6 + pitchRoll * 0.4);
      // Even index -> downhill (negative pitch, gravity adds speed); odd -> uphill.
      const pitch = i % 2 === 0 ? -pitchMag : pitchMag;
      maybeBooster(st, cur, pBooster, laneHalf);
      emitHill(st, cur, laneHalf, pitch);
      maybeHazard(st, cur, pHazard, laneHalf, diff);
    } else if (roll < pStraight + pTurn + pHill + pGap) {
      // GAP segment: a straight run with a hole in the lane to dodge. Length comes
      // from the same straight-length draw so draw order matches a straight.
      const len = rng.range(GEN.straightLength.min, GEN.straightLength.max);
      // VARIED PLACEMENT: one extra draw picks the gap layout. Most gaps leave a
      // skirtable safe LEDGE on one edge (side alternates with the gap counter for
      // a legible weave); a share are FULL-WIDTH holes (no ledge), and that share
      // GROWS with difficulty so late gaps are genuinely committing. The draw is
      // from `makeRng`, so the course stays fully deterministic.
      const layoutRoll = rng.next();
      const pFullWidth = lerp(0.15, 0.55, diff);
      const ledgeSide: -1 | 0 | 1 =
        layoutRoll < pFullWidth ? 0 : st.gapCount % 2 === 0 ? 1 : -1;
      st.gapCount += 1;
      emitGap(st, cur, len, laneHalf, ledgeSide);
      maybeHazard(st, cur, pHazard, laneHalf, diff);
    } else {
      // DELIBERATE LAUNCH-RAMP gambit (laid along the current heading): a short
      // up-wedge in the lane CENTER, a gap right past it, and a long flat landing
      // run. The player CHOOSES to hit the wedge and jump the gap, or steers onto
      // the flat edge strip and threads the gap on the ground. The wedge angle/
      // length escalate with `diff`. `emitLaunchRamp` makes its own fixed draws (so
      // determinism holds); a booster on the flat run-in keeps momentum up so the
      // jump commits.
      maybeBooster(st, cur, pBooster, laneHalf);
      emitLaunchRamp(st, cur, laneHalf, diff);
    }

    // A checkpoint roughly every few segments keeps progress legible and
    // prevents skipping. Always one at the very end (the finish).
    if (i % 3 === 2 && i < segCount - 1) {
      pushCheckpoint(st, cur.pos, false);
    }
  }

  // Final flat run-out + the finish gate (full base width — a fair finishing
  // straight at the far end, rarely reached on a long survival run).
  emitStraight(st, cur, 10, GEN.laneHalfWidth);
  pushCheckpoint(st, cur.pos, true);

  // Pad the world AABB so the camera and kill-plane have margin.
  const bounds: Bounds = {
    min: [st.boundsMin[0] - 4, st.boundsMin[1] - 8, st.boundsMin[2] - 4],
    max: [st.boundsMax[0] + 4, st.boundsMax[1] + 6, st.boundsMax[2] + 4],
  };

  const spawn = { position: spawnPos, rotation: [0, 0, 0, 1] as Quat };

  return {
    schemaVersion: SCHEMA_VERSION,
    simVersion: SIM_VERSION,
    seed: seed >>> 0,
    dateKey,
    bounds,
    spawn,
    tuning: PHYSICS,
    elements: st.elements,
    checkpoints: st.checkpoints,
  };
}
